import styled from "styled-components";
import NavItem from "../../../bootstrap/NavItem";
import Ul from "../../../bootstrap/Ul";

/* Main layout: seat map on the left, booking details on the right */
export const BusSeatMapWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 42%) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  width: 100%;
  padding-bottom: 12px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

/* Left side containing the bus and seat layout */
export const LeftSideWrapper = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

/* Outer bus body */
export const LeftSide = styled.div`
  width: 100%;
  max-width: 340px;
  min-width: 0;
  margin: 0 auto;
  box-sizing: border-box;
  border-top: 7px solid #777;
  border-left: 6px solid #e5e7eb;
  border-right: 6px solid #e5e7eb;
  border-bottom: 6px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

/* Driver/steering area */
export const Steering = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 42px;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;

  img {
    display: block;
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
`;

/* Seat grid wrapper */
export const SeatWrapper = styled.div`
  width: 100%;
  min-width: 0;
  padding: 4px;
  box-sizing: border-box;
  overflow-x: auto;
`;

/* Seat rows */
export const SeatUl = styled(Ul)`
  display: grid;
  grid-template-columns: ${({ nmb }) =>
    `repeat(${Math.max(1, Number(nmb) || 1)}, minmax(0, 1fr))`};
  justify-items: center;
  align-items: center;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 8px 4px;
  list-style: none;
  box-sizing: border-box;
`;

/* Individual seat */
export const SingleSeat = styled(NavItem)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  max-width: 48px;
  min-height: 42px;
  padding: 3px;
  margin: auto;
  border: 1px solid transparent;
  border-radius: 6px;
  box-sizing: border-box;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;

  img {
    display: block;
    width: 38px;
    height: 38px;
    max-width: 100%;
    object-fit: contain;
    pointer-events: none;
  }

  p {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 1px 2px;
    color: #111;
    font-size: 9px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    text-align: center;
    pointer-events: none;
    border-radius: 2px;
    text-shadow: 0 0 2px rgba(255, 255, 255, 0.9);
  }

  &:focus-visible {
    outline: 2px solid #1d4ed8;
    outline-offset: 2px;
  }

  &:hover:not([data-seat-status="unavailable"]):not(
      [data-seat-status="staff"]
    ) {
    border-color: #1f2937;
    transform: scale(1.04);
  }

  &[data-seat-status="available"] {
    background-color: #2e9d58;
    border-color: #238447;
  }

  &[data-seat-status="vip"] {
    background-color: #d4af37;
    border-color: #b08d22;
  }

  &[data-seat-status="selected"] {
    background-color: #4c78a8;
    border-color: #315b88;
  }

  &[data-seat-status="unavailable"],
  &[data-seat-status="staff"] {
    background-color: #bdbdbd;
    border-color: #999;
    cursor: not-allowed;
    opacity: 0.8;
  }

  &[data-seat-status="steering"] {
    background: transparent;
    border-color: transparent;
    cursor: default;
  }

  @media (max-width: 480px) {
    min-height: 36px;
    padding: 2px;

    img {
      width: 32px;
      height: 32px;
    }

    p {
      font-size: 8px;
    }
  }

  @media (max-width: 360px) {
    min-height: 32px;

    img {
      width: 28px;
      height: 28px;
    }

    p {
      font-size: 7px;
    }
  }
`;

/* Right panel: legend, seat summary, boarding/drop-off and fare details */
export const RightSide = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  > * {
    max-width: 100%;
    box-sizing: border-box;
  }

  > * + * {
    margin-top: 10px;
  }
`;

/* Seat legend item */
export const SeatType = styled(NavItem)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 3px 0;
  font-size: 12px;
  line-height: 1.4;
  text-transform: capitalize;
  white-space: nowrap;

  img {
    display: block;
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex-shrink: 0;
  }
`;

/* Section heading */
export const SeatTypeHeader = styled.div`
  margin: 0;
  padding: 8px 0 6px;
  color: #172839;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-transform: uppercase;
`;

/* Passenger/luggage controls */
export const SeatCoutWrapper = styled.div`
  width: 100%;
  min-width: 0;
  margin: 8px 0 12px;
  box-sizing: border-box;
`;

/* Bus facilities section */
export const BusFacilitiesWrapper = styled.div`
  width: 100%;
  min-width: 0;
  margin: 8px 0;
  box-sizing: border-box;
`;

/* Responsive seat legend */
export const SeatLegend = styled(Ul)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  box-sizing: border-box;
`;

/* Reusable colored seat indicator */
export const SeatColorKey = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  border-radius: 3px;
  background-color: ${({ $color }) => $color || "#2e9d58"};
  border: 1px solid ${({ $border }) => $border || "#238447"};
  box-sizing: border-box;
`;
